/**
 * Ambient scribe capture hook.
 *
 * Transcription runs in the browser with the Azure Speech SDK's conversation
 * transcriber, against the clinic's own Azure Speech resource (BAA-covered),
 * and labels speakers in real time. The browser's built-in SpeechRecognition
 * is never used — Chrome/Edge send that audio to consumer cloud services.
 *
 * The scribe WebSocket carries control only: finished phrases go up as
 * append_transcript (server-side safety scan + session record) and the full
 * speaker-labelled transcript is sent once at synthesis time.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  aiErrorMessage,
  checkScribeAccess,
  createScribeWebSocket,
  evaluateCDS,
  generateBHNote,
  saveScribeTranscript,
  transcribeScribeBackfill,
  type CDSAlert,
  type GenerateBHNoteResponse,
  type SafetyHit,
  type ScribeWSEvent,
} from "@/api/ai.api";
import type { AIChartContext, AITemplateType } from "@/lib/ai-chart-context";
import {
  createLiveTranscriber,
  formatTranscript,
  gapLine,
  recoveredLine,
  speakerLabel,
  type LiveTranscriber,
  type TranscriptLine,
} from "@/lib/azure-conversation-transcriber";
import { buildScribePhrases } from "@/lib/scribe-phrases";
import { startOutageRecorder, type OutageRecorder } from "@/lib/outage-recorder";

const VOICE_RMS_THRESHOLD = 0.045;
const CHECKPOINT_INTERVAL_MS = 15_000;
const WS_RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];
const NOTE_WAIT_MS = 180_000;
/** How long Finish waits for outage audio to be transcribed before drafting without it. */
const BACKFILL_WAIT_MS = 60_000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type MicPermissionState = "unknown" | "granted" | "denied" | "unsupported";
export type ScribeConnectionState = "idle" | "connecting" | "connected" | "error";
/** Speech connection health while listening. */
export type ScribeSpeechStatus = "live" | "reconnecting";
/** "azure" = Azure Speech conversation transcriber; "none" = not listening / unavailable. */
export type ScribeSTTMode = "azure" | "none";

export interface UseScribeWebSocketOptions {
  templateType: AITemplateType;
  encounterId?: string;
  patientId?: string;
  chiefComplaint?: string;
  chartContext?: AIChartContext;
  consentAt?: string;
  onTranscriptDelta?: (fullTranscript: string, delta: string) => void;
  onNoteSynthesized?: (response: GenerateBHNoteResponse) => void;
  onCDSAlerts?: (alerts: CDSAlert[]) => void;
  onSafetyAlert?: (hit: SafetyHit) => void;
  onError?: (message: string) => void;
  /** The server's scribe session id (for marking the draft applied/discarded). */
  onSessionStarted?: (sessionId: string) => void;
}

export function useScribeWebSocket(options: UseScribeWebSocketOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const wsRef = useRef<WebSocket | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const transcriberRef = useRef<LiveTranscriber | null>(null);

  /** Finished, speaker-labelled phrases — the client-side source of truth. */
  const linesRef = useRef<TranscriptLine[]>([]);
  const interimRef = useRef<TranscriptLine | null>(null);
  const transcriptRef = useRef("");

  const rafRef = useRef<number | null>(null);
  const levelSmoothRef = useRef(0);
  const lastLevelEmitRef = useRef(0);
  const voiceDetectedRef = useRef(false);

  const [micPermission, setMicPermission] = useState<MicPermissionState>("unknown");
  const [isListening, setIsListening] = useState(false);
  const [speechReady, setSpeechReady] = useState(false);
  const [sttMode, setSttMode] = useState<ScribeSTTMode>("none");
  const [connectionState, setConnectionState] = useState<ScribeConnectionState>("idle");
  const [speechStatus, setSpeechStatus] = useState<ScribeSpeechStatus>("live");
  const [audioLevel, setAudioLevel] = useState(0);

  /** Server scribe session id: checkpoints and socket resumes attach to it. */
  const sessionIdRef = useRef<string | null>(null);
  /** An unfinished session being drafted from its recovered transcript. */
  const recoverySessionIdRef = useRef<string | null>(null);
  const lastCheckpointRef = useRef("");
  const checkpointTimerRef = useRef<number | null>(null);
  /** When the speech connection dropped; null while live. */
  const speechDroppedAtRef = useRef<number | null>(null);
  const outageRecorderRef = useRef<OutageRecorder | null>(null);
  const pendingBackfillsRef = useRef(new Set<Promise<void>>());
  const wsReconnectTimerRef = useRef<number | null>(null);
  const intentionalCloses = useRef(new WeakSet<WebSocket>());
  const reconnectSocketRef = useRef<(attempt: number) => void>(() => {});
  const [voiceDetected, setVoiceDetected] = useState(false);
  const [transcript, setTranscript] = useState("");

  const publishTranscript = useCallback((delta = "") => {
    const full = formatTranscript(linesRef.current, interimRef.current);
    transcriptRef.current = full;
    setTranscript(full);
    optionsRef.current.onTranscriptDelta?.(full, delta);
  }, []);

  // ── Mic level meter (drives the waveform UI with REAL input) ────────────
  const stopLevelLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    levelSmoothRef.current = 0;
    setAudioLevel(0);
  }, []);

  const startLevelLoop = useCallback(() => {
    if (rafRef.current != null) return;
    const tick = () => {
      const analyser = analyserRef.current;
      if (analyser) {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i]! - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        // Fast attack, slow decay - feels responsive without flicker.
        levelSmoothRef.current = Math.max(rms, levelSmoothRef.current * 0.88);

        if (rms > VOICE_RMS_THRESHOLD && !voiceDetectedRef.current) {
          voiceDetectedRef.current = true;
          setVoiceDetected(true);
        }

        const now = performance.now();
        if (now - lastLevelEmitRef.current > 90) {
          lastLevelEmitRef.current = now;
          setAudioLevel(Math.min(1, levelSmoothRef.current * 3.5));
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const teardownAudio = useCallback(() => {
    stopLevelLoop();
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  }, [stopLevelLoop]);

  const buildLevelMeter = useCallback(
    async (stream: MediaStream) => {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      source.connect(analyser);
      startLevelLoop();
    },
    [startLevelLoop],
  );

  // ── WebSocket (control channel) ─────────────────────────────────────────
  const closeSocket = useCallback(() => {
    if (wsReconnectTimerRef.current != null) window.clearTimeout(wsReconnectTimerRef.current);
    wsReconnectTimerRef.current = null;
    if (wsRef.current) {
      intentionalCloses.current.add(wsRef.current);
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionState("idle");
  }, []);

  // ── Transcript checkpoints (crash / closed-tab recovery) ────────────────
  const checkpointTranscript = useCallback(() => {
    const id = sessionIdRef.current;
    const text = formatTranscript(linesRef.current);
    if (!id || !UUID_RE.test(id) || !text || text === lastCheckpointRef.current) return;
    lastCheckpointRef.current = text;
    void saveScribeTranscript(id, text).catch(() => {
      lastCheckpointRef.current = ""; // retry on the next tick
    });
  }, []);

  const stopCheckpoints = useCallback(() => {
    if (checkpointTimerRef.current != null) window.clearInterval(checkpointTimerRef.current);
    checkpointTimerRef.current = null;
  }, []);

  const startCheckpoints = useCallback(() => {
    stopCheckpoints();
    checkpointTimerRef.current = window.setInterval(checkpointTranscript, CHECKPOINT_INTERVAL_MS);
  }, [checkpointTranscript, stopCheckpoints]);

  /**
   * End an outage: insert a placeholder where it happened, then transcribe the
   * audio buffered during it and swap the text in. If that fails, the
   * placeholder becomes a plain "not captured" marker.
   */
  const endOutage = useCallback(
    (gapSeconds: number) => {
      const recorder = outageRecorderRef.current;
      outageRecorderRef.current = null;
      const placeholder = gapLine(gapSeconds, Boolean(recorder));
      linesRef.current.push(placeholder);
      publishTranscript();
      if (!recorder) return;

      const replace = (...lines: TranscriptLine[]) => {
        const i = linesRef.current.indexOf(placeholder);
        if (i === -1) return; // transcript was reset (restart)
        linesRef.current.splice(i, 1, ...lines);
        publishTranscript();
        checkpointTranscript();
      };
      const job = (async () => {
        const clip = await recorder.stop();
        const text = clip ? (await transcribeScribeBackfill(clip.wav, sessionIdRef.current)).text?.trim() : "";
        if (!clip || !text) {
          replace(gapLine(gapSeconds));
          return;
        }
        const missed = gapSeconds - clip.seconds;
        replace(recoveredLine(text), ...(missed > 5 ? [gapLine(missed)] : []));
      })()
        .catch(() => replace(gapLine(gapSeconds)))
        .finally(() => pendingBackfillsRef.current.delete(job));
      pendingBackfillsRef.current.add(job);
    },
    [checkpointTranscript, publishTranscript],
  );

  /** Close an outage that never reconnected (finish/pause mid-outage). */
  const closeOpenGap = useCallback(() => {
    const since = speechDroppedAtRef.current;
    speechDroppedAtRef.current = null;
    setSpeechStatus("live");
    if (since != null) endOutage((Date.now() - since) / 1000);
  }, [endOutage]);

  /** Finish waits briefly for outage audio; anything still pending stays a gap marker. */
  const settleBackfills = useCallback(async () => {
    const pending = [...pendingBackfillsRef.current];
    if (pending.length) {
      await Promise.race([
        Promise.allSettled(pending),
        new Promise((r) => window.setTimeout(r, BACKFILL_WAIT_MS)),
      ]);
    }
    let changed = false;
    linesRef.current = linesRef.current.map((line) => {
      if (!line.pending) return line;
      changed = true;
      return { speaker: "", kind: "gap", text: line.text.replace("recovering", "unable to recover").replace("...", "") };
    });
    if (changed) publishTranscript();
  }, [publishTranscript]);

  const sendControl = useCallback((payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  const handleServerEvent = useCallback((event: ScribeWSEvent) => {
    if (event.event === "scribe_started" && event.data?.session_id) {
      sessionIdRef.current = String(event.data.session_id);
      optionsRef.current.onSessionStarted?.(sessionIdRef.current);
    }
    if (event.event === "note_synthesized") {
      optionsRef.current.onNoteSynthesized?.(event.data as unknown as GenerateBHNoteResponse);
    }
    if (event.event === "safety_alert") {
      optionsRef.current.onSafetyAlert?.(event.data as unknown as SafetyHit);
    }
    if (event.event === "error") {
      optionsRef.current.onError?.(String(event.data?.error ?? "Scribe error"));
    }
  }, []);

  const openSocketAndStart = useCallback(async () => {
    if (wsRef.current) {
      intentionalCloses.current.add(wsRef.current);
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionState("connecting");

    const denied = await checkScribeAccess();
    if (denied) throw new Error(denied);

    const ws = createScribeWebSocket();
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        handleServerEvent(JSON.parse(evt.data) as ScribeWSEvent);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = () => {
      if (intentionalCloses.current.has(ws)) return;
      if (wsRef.current === ws) wsRef.current = null;
      // The transcript lives here, so a dropped socket only pauses the
      // server-side safety scan; reconnect quietly while still listening.
      if (transcriberRef.current) reconnectSocketRef.current(0);
    };

    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error("AI Scribe took too long to connect. Check your internet connection and try again.")),
        8000,
      );
      ws.onopen = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      ws.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error("AI Scribe couldn't connect right now. Please try again in a moment, or contact support if it keeps happening."));
      };
    });

    setConnectionState("connected");
    const resumeId = sessionIdRef.current;
    sendControl({
      action: resumeId ? "resume_scribe" : "start_scribe",
      session_id: resumeId ?? undefined,
      transcript_text: resumeId ? formatTranscript(linesRef.current) : undefined,
      encounter_id: optionsRef.current.encounterId,
      patient_id: optionsRef.current.patientId,
      template_type: optionsRef.current.templateType,
      chief_complaint: optionsRef.current.chiefComplaint,
      patient_context: optionsRef.current.chartContext,
      consent_at: optionsRef.current.consentAt,
    });
  }, [handleServerEvent, sendControl]);

  useEffect(() => {
    reconnectSocketRef.current = (attempt: number) => {
      if (wsReconnectTimerRef.current != null) return;
      const delay = WS_RECONNECT_DELAYS_MS[Math.min(attempt, WS_RECONNECT_DELAYS_MS.length - 1)]!;
      wsReconnectTimerRef.current = window.setTimeout(() => {
        wsReconnectTimerRef.current = null;
        if (!transcriberRef.current || wsRef.current) return;
        openSocketAndStart().catch(() => {
          if (wsRef.current) {
            intentionalCloses.current.add(wsRef.current);
            wsRef.current.close();
            wsRef.current = null;
          }
          reconnectSocketRef.current(attempt + 1);
        });
      }, delay);
    };
  }, [openSocketAndStart]);

  // ── Transcriber ─────────────────────────────────────────────────────────
  const stopTranscriber = useCallback(async () => {
    const t = transcriberRef.current;
    transcriberRef.current = null;
    setSpeechReady(false);
    if (t) await t.stop(); // flushes the last phrase via onPhrase
  }, []);

  /** Mic → Azure transcriber (+ level meter). Keeps lines already captured. */
  const startCapture = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
    setMicPermission("granted");
    mediaStreamRef.current = stream;
    await buildLevelMeter(stream);

    const transcriber = await createLiveTranscriber(stream, {
      onPhrase: (line) => {
        linesRef.current.push(line);
        publishTranscript(line.text);
        sendControl({ action: "append_transcript", transcript_text: `${speakerLabel(line.speaker)}: ${line.text}` });
      },
      onInterim: (line) => {
        interimRef.current = line;
        publishTranscript(line?.text ?? "");
      },
      onError: (message) => {
        setConnectionState("error");
        optionsRef.current.onError?.(message);
      },
      onReconnecting: () => {
        speechDroppedAtRef.current = Date.now();
        setSpeechStatus("reconnecting");
        outageRecorderRef.current = startOutageRecorder(stream);
        checkpointTranscript();
      },
      onReconnected: (gapSeconds) => {
        speechDroppedAtRef.current = null;
        setSpeechStatus("live");
        endOutage(gapSeconds);
        checkpointTranscript();
      },
    }, buildScribePhrases(optionsRef.current.chartContext));
    await transcriber.start();
    transcriberRef.current = transcriber;
    setSttMode("azure");
    setSpeechStatus("live");
    setSpeechReady(true);
  }, [buildLevelMeter, checkpointTranscript, endOutage, publishTranscript, sendControl]);

  // ── Public controls ──────────────────────────────────────────────────────
  const startListening = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPermission("unsupported");
      optionsRef.current.onError?.("Microphone capture is not supported in this browser.");
      throw new Error("mic unsupported");
    }

    // Fresh session - reset all transcript state.
    linesRef.current = [];
    interimRef.current = null;
    transcriptRef.current = "";
    sessionIdRef.current = null;
    recoverySessionIdRef.current = null;
    lastCheckpointRef.current = "";
    speechDroppedAtRef.current = null;
    voiceDetectedRef.current = false;
    setVoiceDetected(false);
    setTranscript("");

    try {
      await openSocketAndStart();
      await startCapture();
      setIsListening(true);
      startCheckpoints();
    } catch (err) {
      if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        setMicPermission("denied");
      }
      setConnectionState("error");
      setSttMode("none");
      optionsRef.current.onError?.(
        aiErrorMessage(err, err instanceof Error ? err.message : "Could not start listening. Use Dictate mode."),
      );
      await stopTranscriber();
      teardownAudio();
      closeSocket();
      setIsListening(false);
      throw err;
    }
  }, [closeSocket, openSocketAndStart, startCapture, startCheckpoints, stopTranscriber, teardownAudio]);

  /** Pause: stop mic + transcriber, KEEP socket and transcript. */
  const pauseListening = useCallback(async () => {
    setIsListening(false);
    await stopTranscriber();
    closeOpenGap();
    interimRef.current = null;
    publishTranscript();
    teardownAudio();
    checkpointTranscript();
  }, [checkpointTranscript, closeOpenGap, publishTranscript, stopTranscriber, teardownAudio]);

  /** Resume after pause: new mic + transcriber, transcript continues. */
  const resumeListening = useCallback(async () => {
    try {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await openSocketAndStart();
      }
      await startCapture();
      setIsListening(true);
    } catch (err) {
      teardownAudio();
      optionsRef.current.onError?.(aiErrorMessage(err, err instanceof Error ? err.message : "Could not resume listening."));
      throw err;
    }
  }, [openSocketAndStart, startCapture, teardownAudio]);

  /** Hard stop (cancel/end/restart): mic, transcriber, everything local. */
  const stopListening = useCallback(() => {
    setIsListening(false);
    stopCheckpoints();
    speechDroppedAtRef.current = null;
    void outageRecorderRef.current?.stop(); // discarded: the session is being thrown away
    outageRecorderRef.current = null;
    setSpeechStatus("live");
    void stopTranscriber();
    teardownAudio();
  }, [stopCheckpoints, stopTranscriber, teardownAudio]);

  /** Resolves "closed" if the socket drops before the note arrives, so the caller can retry over REST. */
  const waitForNoteSynthesized = useCallback(
    (timeoutMs = NOTE_WAIT_MS) =>
      new Promise<GenerateBHNoteResponse | null | "closed">((resolve) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          resolve("closed");
          return;
        }

        let settled = false;
        const onClose = () => {
          if (!intentionalCloses.current.has(ws)) finish("closed");
        };
        const finish = (value: GenerateBHNoteResponse | null | "closed") => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          ws.removeEventListener("close", onClose);
          ws.onmessage = priorOnMessage;
          resolve(value);
        };
        ws.addEventListener("close", onClose);

        const priorOnMessage = ws.onmessage;
        ws.onmessage = (evt) => {
          try {
            const event = JSON.parse(evt.data) as ScribeWSEvent;
            if (event.event === "note_synthesized") {
              const response = event.data as unknown as GenerateBHNoteResponse;
              optionsRef.current.onNoteSynthesized?.(response);
              finish(response);
              return;
            }
            if (event.event === "error") {
              optionsRef.current.onError?.(String(event.data?.error ?? "Note synthesis failed"));
              finish(null);
              return;
            }
          } catch {
            /* continue */
          }
          priorOnMessage?.call(ws, evt);
        };

        const timer = window.setTimeout(() => {
          optionsRef.current.onError?.("Note generation timed out. Try again or use Dictate mode.");
          finish(null);
        }, timeoutMs);
      }),
    [],
  );

  /**
   * Generate the note from text. Used by Dictate mode (REST) and by
   * finishAndSynthesize (WebSocket when the session is live).
   */
  const synthesizeNote = useCallback(
    async (promptOverride?: string) => {
      const prompt = (promptOverride ?? transcriptRef.current).trim();
      if (!prompt) {
        optionsRef.current.onError?.(
          "No speech was captured. Speak for a few seconds, then try Finish again - or use Dictate mode.",
        );
        return null;
      }

      let retryOfSocketDraft = false;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendControl({
          action: "synthesize_note",
          template_type: optionsRef.current.templateType,
          chief_complaint: optionsRef.current.chiefComplaint,
          patient_context: optionsRef.current.chartContext,
          // Client transcript is authoritative; server REPLACES its copy.
          transcript_text: prompt,
        });
        const result = await waitForNoteSynthesized();
        if (result !== "closed") return result;
        retryOfSocketDraft = true;
      }

      try {
        const sessionId = retryOfSocketDraft ? sessionIdRef.current : recoverySessionIdRef.current;
        const response = await generateBHNote({
          encounter_id: optionsRef.current.encounterId,
          template_type: optionsRef.current.templateType,
          chief_complaint: optionsRef.current.chiefComplaint,
          patient_context: optionsRef.current.chartContext,
          prompt,
          // Socket retry: the server hands back the draft the socket already made
          // (or is still making) instead of billing twice. Recovery: the draft
          // is recorded on the unfinished session so it stops showing as unfinished.
          scribe_session_id: sessionId && UUID_RE.test(sessionId) ? sessionId : undefined,
        });
        optionsRef.current.onNoteSynthesized?.(response);
        return response;
      } catch (err) {
        optionsRef.current.onError?.(aiErrorMessage(err, "Could not generate a clinical note. Try again or write it yourself."));
        return null;
      }
    },
    [sendControl, waitForNoteSynthesized],
  );

  /** Finish the ambient session: flush last words, then generate the note. */
  const finishAndSynthesize = useCallback(async () => {
    setIsListening(false);
    stopCheckpoints();
    await stopTranscriber(); // the last phrase arrives via onPhrase before this resolves
    teardownAudio();
    if (interimRef.current?.text) {
      // Recognition stopped before finalizing the tail - keep it.
      linesRef.current.push(interimRef.current);
    }
    closeOpenGap();
    interimRef.current = null;
    publishTranscript();
    await settleBackfills();
    // Saved before synthesis so a failed or abandoned draft stays recoverable.
    checkpointTranscript();

    const combined = transcriptRef.current.trim();
    if (!combined) return null;
    return synthesizeNote(combined);
  }, [checkpointTranscript, closeOpenGap, publishTranscript, settleBackfills, stopCheckpoints, stopTranscriber, synthesizeNote, teardownAudio]);

  const runCDS = useCallback(async (sections: Record<string, string | undefined>) => {
    if (!optionsRef.current.encounterId) return [];
    const alerts = await evaluateCDS({
      encounter_id: optionsRef.current.encounterId,
      template_type: optionsRef.current.templateType,
      has_vitals: Boolean(optionsRef.current.chartContext?.vitals_text),
      sections,
    });
    optionsRef.current.onCDSAlerts?.(alerts);
    return alerts;
  }, []);

  // Checkpoint when the tab is hidden or closing - the last chance before a crash or close.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") checkpointTranscript();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", checkpointTranscript);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", checkpointTranscript);
    };
  }, [checkpointTranscript]);

  useEffect(() => {
    return () => {
      stopCheckpoints();
      void stopTranscriber();
      teardownAudio();
      closeSocket();
    };
  }, [closeSocket, stopCheckpoints, stopTranscriber, teardownAudio]);

  /** Draft the next REST note onto this unfinished session (null to clear). */
  const setRecoverySession = useCallback((id: string | null) => {
    recoverySessionIdRef.current = id;
  }, []);

  return {
    setRecoverySession,
    micPermission,
    isListening,
    speechReady,
    sttMode,
    speechStatus,
    connectionState,
    audioLevel,
    voiceDetected,
    transcript,
    setTranscript,
    startListening,
    pauseListening,
    resumeListening,
    stopListening,
    finishAndSynthesize,
    synthesizeNote,
    runCDS,
    closeSocket,
  };
}
